/**
 * MapLayerPanel — collapsible panel for layer visibility toggles.
 */

import React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import type { LayerVisibility } from '../types/mapTypes';
import type { BoundaryLevel } from '../layers/adminBoundaryLayer';

interface MapLayerPanelProps {
  visibility: LayerVisibility;
  onToggle: (layer: keyof LayerVisibility) => void;
  stats: {
    fieldCount: number;
    treeCount: number;
    farmerCount: number;
    cohortCount: number;
    warehouseCount: number;
  };
  adminBoundaryLevel?: BoundaryLevel;
  onAdminBoundaryChange?: (level: BoundaryLevel) => void;
  onClose: () => void;
}

/** Optional layers map to the module that owns them. */
const LAYER_ACCESS: Partial<Record<keyof LayerVisibility, { feature: string; page: string }>> = {
  trees: { feature: 'traceability', page: 'traceability' },
  farmers: { feature: 'farmers', page: 'farmers' },
  cohorts: { feature: 'cohorts', page: 'cohorts' },
  warehouses: { feature: 'warehouse', page: 'warehouse' },
};

const LAYERS: {
  key: keyof LayerVisibility;
  label: string;
  color: string;
  countKey?: string;
}[] = [
  { key: 'fields', label: 'Farm Fields', color: '#3B82F6', countKey: 'fieldCount' },
  { key: 'trees', label: 'Trees', color: '#16A34A', countKey: 'treeCount' },
  { key: 'farmers', label: 'Farmers', color: '#10B981', countKey: 'farmerCount' },
  { key: 'cohorts', label: 'Cohorts', color: '#2563EB', countKey: 'cohortCount' },
  { key: 'warehouses', label: 'Warehouses', color: '#7C3AED', countKey: 'warehouseCount' },
];

const BOUNDARY_OPTIONS: { value: BoundaryLevel; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'provinces', label: 'Provinces' },
  { value: 'districts', label: 'Districts' },
  { value: 'sectors', label: 'Sectors' },
  { value: 'cells', label: 'Cells' },
];

export default function MapLayerPanel({
  visibility,
  onToggle,
  stats,
  adminBoundaryLevel = 'none',
  onAdminBoundaryChange,
  onClose,
}: MapLayerPanelProps) {
  const { hasFeature, canAccessPage } = useAuth();
  const visibleLayers = LAYERS.filter((layer) => {
    const access = LAYER_ACCESS[layer.key];
    if (!access) return true;
    return hasFeature(access.feature) && canAccessPage(access.page);
  });
  return (
    <div className="w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-bold text-slate-800">Layers</h3>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"
        >
          ✕
        </button>
      </div>

      {/* Layer toggles */}
      <div className="p-3 space-y-1">
        {visibleLayers.map((layer) => {
          const count = layer.countKey ? (stats as any)[layer.countKey] : null;
          return (
            <label
              key={layer.key}
              className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                checked={visibility[layer.key]}
                onChange={() => onToggle(layer.key)}
                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span className="flex items-center gap-2 flex-1 text-sm text-slate-700">
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: layer.color }}
                />
                {layer.label}
              </span>
              {count != null && (
                <span className="text-xs text-slate-400 font-mono">{count}</span>
              )}
            </label>
          );
        })}
      </div>

      {/* Admin boundaries */}
      {onAdminBoundaryChange && (
        <div className="px-3 pb-3 border-t border-slate-100 pt-3">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">
            Admin Boundaries
          </p>
          <select
            value={adminBoundaryLevel}
            onChange={(e) => onAdminBoundaryChange(e.target.value as BoundaryLevel)}
            className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {BOUNDARY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
